/*
 * Product image lightbox, backed by PhotoSwipe 5.
 *
 * The element keeps the <product-modal> name and the show()/hide() pair that
 * Dawn's ModalOpener, quick-add.js and theme-editor.js all expect, but nothing
 * of the old scrolling modal remains: the images are handed to PhotoSwipe,
 * which draws its own overlay, fits one image to the viewport, and handles
 * zoom, pan, swipe and the open/close animation.
 *
 * PhotoSwipe is an ES module and is imported on first open, so a visitor who
 * never clicks an image never downloads it. The URL comes from the markup
 * because an asset_url can only be resolved by Liquid.
 */

if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends HTMLElement {
      constructor() {
        super();
        this.pswp = null;
        this.photoSwipeModule = null;
      }

      /**
       * Called by ModalOpener with the button that was clicked. The button sits
       * inside the gallery slide it belongs to, so the gallery, the slide and
       * therefore the starting index all come from the opener itself -- which
       * also means the duplicate gallery rendered for the 'media on right'
       * layout opens at the right image without needing to know it is a copy.
       */
      async show(opener) {
        const gallery = opener.closest('media-gallery');
        if (!gallery) return;

        const items = this.buildDataSource(gallery);
        if (items.length === 0) return;

        const container = opener.closest('.product-media-container');
        const index = items.findIndex((item) => item.container === container);

        const PhotoSwipe = await this.loadPhotoSwipe();
        if (!PhotoSwipe) return;

        this.open(PhotoSwipe, items, Math.max(index, 0));
      }

      hide() {
        if (this.pswp) this.pswp.close();
      }

      async loadPhotoSwipe() {
        if (this.photoSwipeModule) return this.photoSwipeModule;

        const url = this.dataset.photoswipeUrl;
        if (!url) return null;

        try {
          const module = await import(url);
          this.photoSwipeModule = module.default;
          return this.photoSwipeModule;
        } catch (error) {
          console.error('Could not load PhotoSwipe for the product lightbox:', error);
          return null;
        }
      }

      /**
       * Reads the gallery rather than a duplicated copy of the media list, so
       * what the lightbox shows is always what the gallery is showing.
       */
      buildDataSource(gallery) {
        const items = [];

        gallery.querySelectorAll('.product__media-item').forEach((slide, index) => {
          // Mirrors section-main-product.css: a variant-specific slide is
          // hidden in the gallery unless it is the first one.
          if (index > 0 && slide.classList.contains('product__media-item--variant')) return;

          // Only images carry the data attributes. Video and 3D model slides
          // are skipped -- they play in place in the gallery.
          const container = slide.querySelector('.product-media-container[data-pswp-src]');
          if (!container) return;

          const image = container.querySelector('.product__media img');

          items.push({
            container: container,
            thumbnailElement: image,
            src: container.dataset.pswpSrc,
            width: parseInt(container.dataset.pswpWidth, 10),
            height: parseInt(container.dataset.pswpHeight, 10),
            alt: container.dataset.pswpAlt || '',
            // Shown while the full-size image loads, so the opening animation
            // has something to grow from.
            msrc: image ? image.currentSrc || image.src : undefined,
            thumbCropped: container.classList.contains('media-fit-cover'),
          });
        });

        return items;
      }

      open(PhotoSwipe, items, index) {
        const pswp = new PhotoSwipe({
          dataSource: items,
          index: index,
          // Less than opaque so the blurred page shows through the wash; the
          // blur itself is in component-product-lightbox.css.
          bgOpacity: 0.75,
          maxZoomLevel: parseFloat(this.dataset.maxZoom) || 3,
          errorMsg: this.dataset.errorLabel,
          // The stock chrome is replaced wholesale by registerUi below.
          arrowPrev: false,
          arrowNext: false,
          counter: false,
          close: false,
          zoom: false,
        });

        pswp.addFilter('thumbEl', (thumbEl, data) => data.thumbnailElement || thumbEl);
        pswp.on('uiRegister', () => this.registerUi(pswp, items.length));

        // Fires once the overlay has been painted at its starting state, so
        // the blur transitions in with the wash rather than landing at full
        // strength in the first frame.
        pswp.on('openingAnimationStart', () => {
          if (pswp.element) pswp.element.classList.add('pswp--frosted');
        });

        // 'close' fires at the top of the hide animation. The blur and the
        // scroll lock both come off here so the frosting clearing and the
        // scrollbar returning happen as one thing, not 333ms apart.
        pswp.on('close', () => {
          if (pswp.element) pswp.element.classList.remove('pswp--frosted');
          this.releaseScroll();
        });

        pswp.on('destroy', () => {
          // Belt and braces: 'close' has normally already run.
          this.releaseScroll();
          this.removeAttribute('open');
          this.pswp = null;
        });

        this.pswp = pswp;
        this.setAttribute('open', '');
        this.lockScroll();
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

        pswp.init();
      }

      /**
       * Locking the body hides the scrollbar, which widens the viewport and
       * shifts the whole page sideways underneath the lightbox -- invisible on
       * the way in, very visible on the way out. Holding the scrollbar's width
       * back as padding keeps the content exactly where it was.
       *
       * PhotoSwipe blocks wheel and touch scrolling on its own, so this only
       * has to stop the stray cases: keyboard scrolling and dragging the
       * scrollbar itself.
       */
      lockScroll() {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
        document.body.classList.add('overflow-hidden');
      }

      releaseScroll() {
        document.body.classList.remove('overflow-hidden');
        document.body.style.paddingRight = '';
      }

      /**
       * Previous, close and next, in that order, as a centred row along the
       * bottom. PhotoSwipe places every registered element inside its top bar
       * regardless of name; component-product-lightbox.css moves the bar.
       */
      registerUi(pswp, itemCount) {
        pswp.ui.registerElement({
          name: 'close-button',
          className: 'lightbox-button lightbox-button--close',
          ariaLabel: this.dataset.closeLabel,
          order: 2,
          isButton: true,
          html: `
            <svg aria-hidden="true" focusable="false" fill="none" viewBox="0 0 16 16">
              <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>
            </svg>
          `,
          onClick: () => pswp.close(),
        });

        if (itemCount < 2) return;

        pswp.ui.registerElement({
          name: 'previous-button',
          className: 'lightbox-button lightbox-button--previous',
          ariaLabel: this.dataset.previousLabel,
          order: 1,
          isButton: true,
          html: `
            <svg aria-hidden="true" focusable="false" fill="none" viewBox="0 0 16 18">
              <path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/>
            </svg>
          `,
          onClick: () => pswp.prev(),
        });

        pswp.ui.registerElement({
          name: 'next-button',
          className: 'lightbox-button lightbox-button--next',
          ariaLabel: this.dataset.nextLabel,
          order: 3,
          isButton: true,
          html: `
            <svg aria-hidden="true" focusable="false" fill="none" viewBox="0 0 16 18">
              <path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/>
            </svg>
          `,
          onClick: () => pswp.next(),
        });
      }
    }
  );
}
